import { useEffect } from "react";
import { useRevalidator } from "react-router";
import { useI18n } from "../i18n/index.js";
import type { WorkspaceApi, WorkspaceSummary } from "../lib/api.js";
import { invalidateSession } from "../lib/session.js";
import { ConnectorIcon, LinkButton, SpinnerIcon } from "../ui/index.js";
import { EmptyState } from "./states.jsx";

const POLL_MS = 5000;

/** While a setup read runs, ask every few seconds; when it is over, the session and the page catch up by themselves. */
export function useSetupPoll(running: boolean, api: WorkspaceApi) {
  const revalidator = useRevalidator();
  useEffect(() => {
    if (!running) return;
    let stopped = false;
    const check = async () => {
      const setup = await api.setup().catch(() => null);
      if (stopped || !setup || setup.state === "running") return;
      stopped = true;
      invalidateSession();
      await revalidator.revalidate();
    };
    void check();
    const timer = setInterval(() => void check(), POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [running, api, revalidator]);
}

/**
 * What a workspace page shows while the workspace is not set up (D-0022): its sources are being read, or
 * none were chosen. Nothing else — an empty page here would read as "nothing decided", which nobody knows yet.
 * While the read runs it asks every few seconds, and the page comes back by itself when it is over.
 */
export function SetupGate({ workspace, api }: { workspace: WorkspaceSummary & { setup: NonNullable<WorkspaceSummary["setup"]> }; api: WorkspaceApi }) {
  const { t } = useI18n();
  const running = workspace.setup === "running";
  useSetupPoll(running, api);

  return (
    <div className="dt-setup-gate" data-testid="setup-gate">
      {running ? (
        <EmptyState title={t("setupGate.running")} icon={<SpinnerIcon size={18} />} />
      ) : (
        <EmptyState
          title={t("setupGate.pending")}
          icon={<ConnectorIcon size={18} />}
          action={
            <LinkButton to={`/w/${workspace.slug}/setup`} variant="primary" size="sm">
              {t("setupGate.add")}
            </LinkButton>
          }
        />
      )}
    </div>
  );
}
